import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import DrawerMenu from './components/DrawerMenu/DrawerMenu';
import Header from './components/Header/Header';
import SimpleHeader from './components/Header/SimpleHeader';
import AuthHeader from './components/Header/AuthHeader';
import ProductHeader from './product/components/ProductHeader/ProductHeader';
import {TermsPage, PrivacyPage, SupportPage, SubscriptionTermsPage} from './pages/Legal/LegalPages';
import PlanSelection from './pages/PlanSelection';
import './App.css';
import './i18n/config';
import { useTranslation } from 'react-i18next';
import UserProfile from './product/UserProfile/UserProfile';
import Login from './product/Login/Login';
import ProtectedRoute from './components/ProtectedRoute';
import PinCodeSetUp from './product/PinCodeSetUp/PinCodeSetUp';
import ConfirmPinCodePage from "./product/PinCodeSetUp/ConfirmPinCodePage";
import ResetPin from './product/ResetPin';
import ForgotPin from './product/ForgotPin';
import ContactForm from './product/Help/ContactForm';
import { useAuthStore } from './store/useAuthStore';
import News from './product/News/News';
import Courses from './product/Courses/Courses';
import ChangeEmail from './product/components/ChangeEmail/ChangeEmail';
import DeleteAccount from './product/components/DeleteAccount/DeleteAccount';
import Subscription from './product/components/Subscription/Subscription';
import Feedback from './product/components/Feedback/Feedback';
import LanguageConfig from './product/components/LanguageConfig/LanguageConfig';
import ConfirmEmailChange from './product/components/ConfirmEmailChange/ConfirmEmailChange';
import { getCurrentUser } from './api/productService';
import BottomNav from './components/BottomNav/BottomNav';
import ToastProvider from "./components/Toast/ToastProvider";
import {ToastContainer} from "./components/Toast";
import CourseDetailWrapper from './product/CourseDetail/CourseDetailWrapper';
import CourseStartPage from './product/CourseStartPage/CourseStartPage';
import CourseLearning from './product/CourseLearning/CourseLearning';
import {listOfLang} from "./i18n/config";
import NotFound from './pages/NotFound';
import {AllCourses} from "./pages/AllCourses/AllCourses";
import {useAppState} from "./store/useAppState";
import { SvgCacheProvider } from './components/IconSVG/SvgCacheContext';
import {ViewWrapper} from "./components/ViewWrapper/ViewWrapper";
import HelpPage from './product/Help/HelpPage/HelpPage';
import Answer from './product/Help/Answer/Answer';
import { setNavigator } from './utils/smartNavigate';
import {useDisplayMode} from "./hooks/useDisplayMode";
import {DesktopTopNav} from "./components/DesktopTopNav/DesktopTopNav";
import {ProfileSettingsHeader} from "./components/ProfileSettingsHeader/ProfileSettingsHeader";
const LAST_VISITED_STEP_KEY = 'last_visited_step';

const NavigationRegistrar: React.FC = () => {
    const navigate = useNavigate();
    useEffect(() => {
        setNavigator(navigate);
        return () => setNavigator(null);
    }, [navigate]);
    return null;
};

const AppContent: React.FC = () => {
    const mainRef = useRef<HTMLDivElement | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isLanguageInitialized, setIsLanguageInitialized] = useState(false);
    const location = useLocation();
    const { i18n, t } = useTranslation();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const {setCoursesFilterOpen: setIsFilterOpen} = useAppState((state) => state);
    const {isDesktop, isMobile} = useDisplayMode();

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    // Show back button on all pages except the first quiz step
    const showBackButton = !(location.pathname === '/quiz/f1/1/v1' || location.pathname === '/');

    // Check if current page should use ProductHeader
    const useProductHeader = ['/courses', '/news', '/dashboard'].includes(location.pathname);

    // Check if current page should use SimpleHeader
    const useSimpleHeader = ['/subscription', '/delete-account', '/change-email', '/feedback', '/language', '/all-courses', '/help-answer', '/help-contact'].includes(location.pathname);

    // Check if current page should use AuthHeader (for non-authenticated pages and PIN setup)
    const useAuthHeader = (!isAuthenticated && ['/login'].includes(location.pathname)) || ['/pin', '/confirm-pin', '/reset-pin', '/restore-pass'].includes(location.pathname);

    // Check if we should show back button in AuthHeader (for PIN step only, not PIN pages)
    const showAuthHeaderBackButton = useAuthHeader && location.search.includes('step=pin') && !['/pin', '/confirm-pin'].includes(location.pathname);

    // Check if current page should use CourseDetailHeader (for course detail pages)
    const useCourseDetailHeader = location.pathname.match(/^\/course\/\d+$/);

    const useProfileSettingsDesktopHeader = ['/terms', '/subscription', '/feedback', '/language', '/privacy', '/subscription-terms', '/delete-account'].includes(location.pathname) && isDesktop;

    // Check if current page is a learning page (has its own header)
    const isLearningPage = location.pathname.match(/^\/course\/\d+\/lesson\/\d+\/learn$/);

    // Get the title for SimpleHeader based on the current path
    const getSimpleHeaderTitle = () => {
        switch (location.pathname) {
            case '/subscription':
                return t('product.subscription.title');
            case '/delete-account':
                return t('product.deleteAccount.title');
            case '/change-email':
                return t('product.changeEmail.title');
            case '/feedback':
                return t('product.feedback.title');
            case '/language':
                return t('product.language.title');
            case '/all-courses':
                return t('product.courses.recommendedForYou');
          case '/privacy':
              return t('product.profile.settings.privacy.title');
              case '/terms':
                return t('product.profile.settings.terms.title');
          case '/subscription-terms':
            return t('product.profile.settings.subscription.title');
            default:
                return '';
        }
    };

    const getComputedClasses = () => {
      switch (location.pathname) {
        case '/login':
        case '/pin':
        case '/confirm-pin':
        case '/reset-pin':
        case '/restore-pass':
          return 'content without-bottom-nav';
        case '/help-answer':
        case '/help-contact':
          return 'content-help'
        default:
          return 'content';
      }
    }
    // Get the right button configuration for SimpleHeader
    const getSimpleHeaderRightButton = () => {
        if (location.pathname === '/all-courses') {
            return {
                icon: '/images/ui/filter.png',
                onClick: () => {
                    // TODO: Implement filter functionality
                    console.log('Filter button clicked');
                    setIsFilterOpen(true);
                },
                ariaLabel: 'Filter courses'
            };
        }
        return undefined;
    };

    // Pages that should not show any header or bottom navigation
    const hideHeaderAndNav = ['/subscription/success', '/subscription/failed', '/confirm-email-change'].includes(location.pathname);

    // Pages that should hide only bottom navigation (but keep header)
    const hideBottomNav = ['/pin', '/confirm-pin'].includes(location.pathname);

    // Initialize language and step on component mount
    useEffect(() => {
        const initializeApp = async () => {
            try {
                let userLang;

                if (isAuthenticated) {
                    try {
                        // Try to get language from user profile
                        const userProfile = await getCurrentUser();
                        if (userProfile?.user.lang) {
                            userLang = userProfile.user.lang;
                        }
                    } catch (error) {
                        console.error('Error fetching user profile:', error);
                    }
                }

                // Fallback to browser language or stored preference if not authenticated
                // or if profile fetch failed
                if (!userLang) {
                    userLang = localStorage.getItem('user_language') ||
                              navigator.language.split('-')[0];
                }
                if(!listOfLang.includes(userLang)){
                    userLang = 'en';
                }

                // Map 'us' to 'en' since we only support 'en' locale
                if (userLang === 'us') {
                    userLang = 'en';
                }
                if(userLang === 'uk'){
                    userLang = 'en'
                }

                // Store the language preference
                localStorage.setItem('user_language', userLang);

                // Change language without causing re-render issues
                await i18n.changeLanguage(userLang);

                // Get initial step
                const lastVisitedStep = localStorage.getItem(LAST_VISITED_STEP_KEY);
                if (lastVisitedStep) {
                    const parsedStep = parseInt(lastVisitedStep, 10);
                    if (!isNaN(parsedStep) && parsedStep > 0) {
                        // step = parsedStep;
                    }
                }

                setIsLanguageInitialized(true);
            } catch (error) {
                console.error('Error initializing app:', error);
                setIsLanguageInitialized(true);
            }
        };

        initializeApp();
    }, [i18n, isAuthenticated]);

    // Scroll to top of main content when path changes
    useEffect(() => {
        // Defer to ensure DOM updates finished
        if (mainRef.current) {
            try {
                mainRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            } catch (e) {
                // Fallback for older browsers
                mainRef.current.scrollTop = 0;
                (mainRef.current as any).scrollLeft = 0;
            }
        }
    }, [location.pathname]);

    // Don't render routes until language is initialized to prevent redirect loop
    if (!isLanguageInitialized) {
        return (
            <div className="app">
                <div></div>
            </div>
        );
    }

    function getAppClasses() {
      switch (location.pathname) {
        case '/profile':
        case '/terms':
        case '/privacy':
        case '/subscription-terms':
        case '/delete-account':
        case '/language':
        case '/feedback':
        case '/subscription':
        case '/news':
          return 'profile_app';
          default:
            return '';
      }
    }

    return (
      <>
        <ViewWrapper enable={isAuthenticated && !hideHeaderAndNav && !hideBottomNav && isDesktop && !useProfileSettingsDesktopHeader}>
          <DesktopTopNav />
        </ViewWrapper>
        <ViewWrapper enable={useProfileSettingsDesktopHeader}>
          <ProfileSettingsHeader title={getSimpleHeaderTitle()}/>
        </ViewWrapper>
        <div className={`app ${getAppClasses()}`}>
          <NavigationRegistrar />
          <div ref={mainRef} className={getComputedClasses()}>
            <ViewWrapper enable={!hideHeaderAndNav && location.pathname !== '/profile' && !useCourseDetailHeader && !isLearningPage}>
                <ViewWrapper enable={useProductHeader && isMobile}><ProductHeader streakCount={0} /></ViewWrapper>
              <ViewWrapper enable={useSimpleHeader && !isDesktop}>
                <SimpleHeader
                  title={getSimpleHeaderTitle()}
                  rightButton={getSimpleHeaderRightButton()}
                />
              </ViewWrapper>
              <ViewWrapper enable={useAuthHeader}>
                <AuthHeader showBackButton={showAuthHeaderBackButton} />
              </ViewWrapper>
              <ViewWrapper enable={!(useProductHeader || useSimpleHeader || useAuthHeader || useProfileSettingsDesktopHeader)}>
                <Header onMenuClick={toggleDrawer} showBackButton={showBackButton} />
              </ViewWrapper>
            </ViewWrapper>
            <main className={isAuthenticated ? 'app__content_product' : 'app__content'}>
              <DrawerMenu isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/plans" element={<PlanSelection />} />
                <Route path="/dashboard" element={<Courses />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/subscription-terms" element={<SubscriptionTermsPage />} />
                <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
                <Route path="/login" element={<Login />} />
                <Route path="/pin" element={<PinCodeSetUp />} />
                <Route path="/confirm-pin" element={<ConfirmPinCodePage />} />
                <Route path="/reset-pin" element={<ResetPin />} />
                <Route path="/restore-pass" element={<ForgotPin />} />
                <Route path="/help-contact" element={<ContactForm />} />
                <Route path="/help-answer" element={<Answer />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/all-courses" element={<AllCourses />} />
                  <Route path="/change-email" element={<ChangeEmail />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/feedback" element={<Feedback />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/help-contact" element={<ContactForm />} />
                  <Route path="/help-answer" element={<Answer />} />
                  <Route path="/language" element={<LanguageConfig />} />
                  <Route path="/subscription" element={<Subscription />} />
                  <Route path="/course/:id" element={<CourseDetailWrapper />} />
                  <Route path="/course/:courseId/lesson/:lessonId/start" element={<CourseStartPage />} />
                  <Route path="/course/:courseId/lesson/:lessonId/learn" element={<CourseLearning />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </div>
          {isAuthenticated && !hideHeaderAndNav && !hideBottomNav && isMobile && <BottomNav />}
        </div>
      </>
    );
};

const RootRedirect: React.FC = () => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    if (isAuthenticated) {
        return <Navigate to="/profile" replace />;
    }
    return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
    return (
        <ToastProvider>
            <SvgCacheProvider>
                <Router>
                    <AppContent />
                </Router>
                <ToastContainer />
            </SvgCacheProvider>
        </ToastProvider>
    );
};

export default App;
